/**
 * Recent Searches Management Utility
 *
 * Stores and retrieves recent search queries from localStorage
 * with automatic deduplication and size management.
 *
 * @author USLand UX Team
 */

export interface RecentSearch {
  query: string;
  timestamp: number;
}

const STORAGE_KEY = 'usland_recent_searches';
const MAX_RECENT_SEARCHES = 10;

/**
 * Get recent searches from localStorage
 */
export function getRecentSearches(): RecentSearch[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const searches = JSON.parse(stored) as RecentSearch[];
    return searches.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error loading recent searches:', error);
    return [];
  }
}

/**
 * Add a search query to recent searches
 * Automatically deduplicates and maintains max size
 */
export function addRecentSearch(query: string): void {
  if (typeof window === 'undefined') return;
  if (!query.trim()) return;

  try {
    const searches = getRecentSearches();

    // Remove existing entry if present (will be re-added at top)
    const filtered = searches.filter(s => s.query.toLowerCase() !== query.toLowerCase());

    // Add new search at the beginning
    const updated: RecentSearch[] = [
      { query: query.trim(), timestamp: Date.now() },
      ...filtered
    ];

    // Keep only the most recent MAX_RECENT_SEARCHES
    const trimmed = updated.slice(0, MAX_RECENT_SEARCHES);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error saving recent search:', error);
  }
}

/**
 * Clear all recent searches
 */
export function clearRecentSearches(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing recent searches:', error);
  }
}

/**
 * Remove a specific search from recent searches
 */
export function removeRecentSearch(query: string): void {
  if (typeof window === 'undefined') return;

  try {
    const searches = getRecentSearches();
    const filtered = searches.filter(s => s.query.toLowerCase() !== query.toLowerCase());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing recent search:', error);
  }
}

/**
 * Detect if a query looks like a property ID
 * Pattern: USL-XXXXX or USLXXXXX (case insensitive)
 */
export function isPropertyId(query: string): boolean {
  const trimmed = query.trim();
  // Match USL-12345, USL12345, usl-12345, #USL-12345, etc.
  return /^#?usl-?\d+$/i.test(trimmed);
}

/**
 * Extract property ID from query
 * Converts "USL-12345" or "USL12345" to clean format
 */
export function extractPropertyId(query: string): string | null {
  const trimmed = query.trim().replace(/^#/, ''); // Remove leading #
  const match = trimmed.match(/^(usl-?\d+)$/i);

  if (!match || !match[1]) return null;

  // Normalize to USL-XXXXX format
  const id = match[1].toUpperCase();
  if (id.includes('-')) return id;

  // Add hyphen if missing: USL12345 -> USL-12345
  return id.replace(/^USL(\d+)$/, 'USL-$1');
}
